import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ModelosManagementPanel() {
  const [models, setModels] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const fetchModels = async () => {
    try {
      const r = await axios.get(`${API}/models`, { withCredentials: true });
      setModels(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      try {
        const f = await axios.get(`${API}/models/list`, { withCredentials: true });
        setModels(Array.isArray(f.data) ? f.data : []);
      } catch {
        setModels([]);
      }
    }
  };

  useEffect(() => {
    fetchModels();
  }, []);

  const startEdit = (m) => {
    setEditing({ ...m, social_links: m.social_links || {}, tip_menu: m.tip_menu || [], platforms: m.platforms && m.platforms.length ? m.platforms : ["onlyfans"] });
    setOpen(true);
  };

  const togglePlatform = (pf) => {
    const current = editing?.platforms || [];
    const has = current.includes(pf);
    const next = has ? current.filter((x) => x !== pf) : [...current, pf];
    setEditing({ ...editing, platforms: next.length ? next : ["onlyfans"] });
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("La foto debe pesar menos de 2 MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => setEditing({ ...editing, photo_url: ev.target.result });
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!editing) return;
    try {
      await axios.put(
        `${API}/models/${editing.id}`,
        {
          name: editing.name,
          biografia: editing.biografia,
          usuarios_potenciales: editing.usuarios_potenciales,
          metodologia_chat: editing.metodologia_chat,
          expresion_chat: editing.expresion_chat,
          status: editing.status,
          porcentaje_agencia: editing.porcentaje_agencia,
          año_ingreso: editing.año_ingreso,
          photo_url: editing.photo_url,
          personalidad: editing.personalidad,
          social_links: editing.social_links || {},
          precio_custom_video: editing.precio_custom_video,
          precio_custom_photo: editing.precio_custom_photo,
          precio_videollamada: editing.precio_videollamada,
          tip_menu: editing.tip_menu || [],
          platforms: editing.platforms && editing.platforms.length ? editing.platforms : ["onlyfans"],
        },
        { withCredentials: true }
      );
      toast.success("Modelo actualizada");
      setOpen(false);
      setEditing(null);
      fetchModels();
    } catch {
      toast.error("Error al guardar");
    }
  };

  const filtered = models.filter((m) => m.name?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-4">
      <Card className="bg-[#0a0a0a] border border-white/[0.08] rounded-2xl p-4">
        <Input
          placeholder="Buscar modelo..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="bg-zinc-800 border-zinc-700 text-white"
        />
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((m) => (
          <Card
            key={m.id}
            onClick={() => startEdit(m)}
            className="bg-[#0a0a0a] border border-white/[0.08] hover:border-pink-500/40 rounded-2xl p-4 cursor-pointer transition-all"
            data-testid={`modelo-card-${m.id}`}
          >
            <div className="flex items-start gap-3">
              {m.photo_url ? (
                <img src={m.photo_url} alt={m.name} className="w-16 h-16 rounded-xl object-cover border-2 border-pink-500/40 shrink-0" />
              ) : (
                <div className="w-16 h-16 rounded-xl bg-zinc-800 border-2 border-zinc-700 flex items-center justify-center text-2xl shrink-0">👤</div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex justify-between items-start gap-2">
                  <h3 className="text-white font-bold text-base truncate">{m.name}</h3>
                  <span className={`text-[10px] px-2 py-0.5 rounded shrink-0 ${m.status === "activa" ? "bg-green-900/30 text-green-400" : "bg-red-900/30 text-red-400"}`}>
                    {m.status}
                  </span>
                </div>
                {m.personalidad && <p className="text-pink-300 text-xs italic line-clamp-1 mt-1">🎭 {m.personalidad}</p>}
                <div className="flex flex-wrap gap-1 mt-2">
                  {(m.tip_menu || []).length > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-300">💵 {m.tip_menu.length}</span>}
                  {Object.values(m.social_links || {}).some((v) => v) && <span className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-300">🔗</span>}
                  {m.porcentaje_agencia && <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-500/20 text-green-300">{m.porcentaje_agencia}%</span>}
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* EDIT DIALOG */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[95vw] max-w-lg bg-zinc-900 border-zinc-700 text-white max-h-[90vh] overflow-y-auto overflow-x-hidden">
          <DialogHeader><DialogTitle className="text-white">Editar Modelo</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4 pb-4">
              {/* FOTO */}
              <div className="flex flex-col items-center gap-2 pb-3 border-b border-white/10">
                {editing.photo_url ? (
                  <img src={editing.photo_url} alt={editing.name} className="w-28 h-28 rounded-2xl object-cover border-2 border-pink-500/50" />
                ) : (
                  <div className="w-28 h-28 rounded-2xl bg-zinc-800 border-2 border-zinc-700 flex items-center justify-center text-3xl">👤</div>
                )}
                <label className="cursor-pointer text-pink-400 hover:text-pink-300 text-sm">
                  📷 {editing.photo_url ? "Cambiar foto" : "Subir foto"}
                  <input type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" />
                </label>
              </div>

              <div><Label className="text-gray-300">Nombre</Label>
                <Input value={editing.name || ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white" />
              </div>

              <div><Label className="text-gray-300">Estado</Label>
                <Select value={editing.status || "activa"} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    <SelectItem value="activa" className="text-white">Activa</SelectItem>
                    <SelectItem value="inactiva" className="text-white">Inactiva</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div><Label className="text-gray-300">🎭 Personalidad</Label>
                <textarea value={editing.personalidad || ""} onChange={(e) => setEditing({ ...editing, personalidad: e.target.value })} className="w-full min-h-[70px] p-2 bg-zinc-800 border border-zinc-700 rounded-md text-white text-sm" />
              </div>

              <div><Label className="text-gray-300">Biografía</Label>
                <textarea value={editing.biografia || ""} onChange={(e) => setEditing({ ...editing, biografia: e.target.value })} className="w-full min-h-[60px] p-2 bg-zinc-800 border border-zinc-700 rounded-md text-white text-sm" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-gray-300 text-xs">% Agencia</Label>
                  <Input type="number" value={editing.porcentaje_agencia || ""} onChange={(e) => setEditing({ ...editing, porcentaje_agencia: parseFloat(e.target.value) || 0 })} className="bg-zinc-800 border-zinc-700 text-white" />
                </div>
                <div><Label className="text-gray-300 text-xs">Año ingreso</Label>
                  <Input type="number" value={editing.año_ingreso || ""} onChange={(e) => setEditing({ ...editing, año_ingreso: parseInt(e.target.value) || null })} className="bg-zinc-800 border-zinc-700 text-white" />
                </div>
              </div>

              {/* PLATAFORMAS */}
              <div className="space-y-2 p-3 bg-purple-500/5 border border-purple-500/20 rounded-lg">
                <Label className="text-purple-300">🌐 Plataformas activas</Label>
                <p className="text-xs text-gray-500 -mt-1">Selecciona las plataformas donde esta modelo tiene cuenta activa.</p>
                <div className="flex flex-wrap gap-2 pt-1">
                  {[
                    { id: "onlyfans", label: "OnlyFans", color: "from-blue-500 to-cyan-500" },
                    { id: "mym_fans", label: "MYM Fans", color: "from-pink-500 to-rose-500" },
                    { id: "pornhub", label: "PornHub", color: "from-orange-500 to-amber-500" },
                  ].map((pf) => {
                    const active = (editing.platforms || ["onlyfans"]).includes(pf.id);
                    return (
                      <button
                        key={pf.id}
                        type="button"
                        data-testid={`platform-${pf.id}`}
                        onClick={() => togglePlatform(pf.id)}
                        className={`px-3 py-1.5 rounded-lg text-sm border transition-all ${
                          active
                            ? `bg-gradient-to-r ${pf.color} text-white border-transparent shadow`
                            : "bg-zinc-800 border-zinc-700 text-gray-400 hover:border-purple-500/50"
                        }`}
                      >
                        {active ? "✓ " : ""}{pf.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* REDES */}
              <div className="space-y-2 p-3 bg-pink-500/5 border border-pink-500/20 rounded-lg">
                <Label className="text-pink-300">🔗 Redes Sociales</Label>
                {["instagram", "tiktok", "twitter", "onlyfans", "fansly", "otro"].map((p) => (
                  <div key={p} className="flex items-center gap-2">
                    <span className="text-xs text-gray-400 w-20 capitalize shrink-0">{p}:</span>
                    <Input
                      value={(editing.social_links || {})[p] || ""}
                      onChange={(e) => setEditing({ ...editing, social_links: { ...(editing.social_links || {}), [p]: e.target.value } })}
                      placeholder="https://..."
                      className="bg-zinc-800 border-zinc-700 text-white text-sm h-8"
                    />
                  </div>
                ))}
              </div>

              {/* TARIFAS */}
              <div className="space-y-3 p-3 bg-green-500/5 border border-green-500/20 rounded-lg">
                <Label className="text-green-300">💰 Tarifas (USD)</Label>
                <div className="grid grid-cols-3 gap-2">
                  <div><Label className="text-gray-300 text-[11px]">Video</Label>
                    <Input type="number" value={editing.precio_custom_video || 0} onChange={(e) => setEditing({ ...editing, precio_custom_video: parseFloat(e.target.value) || 0 })} className="bg-zinc-800 border-zinc-700 text-white text-sm" />
                  </div>
                  <div><Label className="text-gray-300 text-[11px]">Foto</Label>
                    <Input type="number" value={editing.precio_custom_photo || 0} onChange={(e) => setEditing({ ...editing, precio_custom_photo: parseFloat(e.target.value) || 0 })} className="bg-zinc-800 border-zinc-700 text-white text-sm" />
                  </div>
                  <div><Label className="text-gray-300 text-[11px]">Llamada</Label>
                    <Input type="number" value={editing.precio_videollamada || 0} onChange={(e) => setEditing({ ...editing, precio_videollamada: parseFloat(e.target.value) || 0 })} className="bg-zinc-800 border-zinc-700 text-white text-sm" />
                  </div>
                </div>
              </div>

              {/* TIP MENU */}
              <div className="space-y-2 p-3 bg-yellow-500/5 border border-yellow-500/20 rounded-lg">
                <div className="flex items-center justify-between">
                  <Label className="text-yellow-300">💵 Tip Menu</Label>
                  <button
                    type="button"
                    onClick={() => setEditing({ ...editing, tip_menu: [...(editing.tip_menu || []), { nombre: "", precio: 0, precio_max: 0 }] })}
                    className="text-yellow-400 hover:text-yellow-300 text-xs font-medium"
                  >
                    + Agregar
                  </button>
                </div>
                {(editing.tip_menu || []).length === 0 ? (
                  <p className="text-xs text-gray-500 italic">Sin acciones definidas.</p>
                ) : (
                  (editing.tip_menu || []).map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <Input
                        value={item.nombre || ""}
                        onChange={(e) => {
                          const tm = [...(editing.tip_menu || [])];
                          tm[idx] = { ...tm[idx], nombre: e.target.value };
                          setEditing({ ...editing, tip_menu: tm });
                        }}
                        placeholder="Nombre acción"
                        className="bg-zinc-800 border-zinc-700 text-white text-sm h-8 flex-1 min-w-0"
                      />
                      <span className="text-gray-400 text-sm shrink-0">$</span>
                      <Input
                        type="number"
                        value={item.precio || 0}
                        onChange={(e) => {
                          const tm = [...(editing.tip_menu || [])];
                          tm[idx] = { ...tm[idx], precio: parseFloat(e.target.value) || 0 };
                          setEditing({ ...editing, tip_menu: tm });
                        }}
                        placeholder="min"
                        className="bg-zinc-800 border-zinc-700 text-white text-sm h-8 w-16"
                      />
                      <span className="text-gray-500 text-xs shrink-0">a</span>
                      <Input
                        type="number"
                        value={item.precio_max || 0}
                        onChange={(e) => {
                          const tm = [...(editing.tip_menu || [])];
                          tm[idx] = { ...tm[idx], precio_max: parseFloat(e.target.value) || 0 };
                          setEditing({ ...editing, tip_menu: tm });
                        }}
                        placeholder="max"
                        className="bg-zinc-800 border-zinc-700 text-white text-sm h-8 w-16"
                      />
                      <button
                        type="button"
                        onClick={() => setEditing({ ...editing, tip_menu: (editing.tip_menu || []).filter((_, i) => i !== idx) })}
                        className="text-red-400 hover:text-red-300 shrink-0 px-1"
                      >
                        🗑️
                      </button>
                    </div>
                  ))
                )}
              </div>

              <Button onClick={handleSave} className="w-full bg-pink-600 hover:bg-pink-700">Guardar Cambios</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
